/**
 * Profitability Panel Component
 * 
 * Displays profitability data for selected vehicles in FleetIntelligence
 */

import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertCircle, Info, Check, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  getDailyProfitability, 
  getFleetProfitabilitySummary,
  getProfitabilityStatus,
  type DailyProfitability,
  type FleetProfitabilitySummary,
  type DataAvailabilityStatus 
} from '../../services/profitabilityApi';
import type { TimeRange } from '../../tabs/FleetIntelligence';

interface ProfitabilityPanelProps {
  selectedVehicles: string[];
  timeRange: TimeRange;
}

export default function ProfitabilityPanel({ selectedVehicles, timeRange }: ProfitabilityPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleData, setVehicleData] = useState<DailyProfitability[]>([]);
  const [fleetSummary, setFleetSummary] = useState<FleetProfitabilitySummary | null>(null);
  const [dataStatus, setDataStatus] = useState<DataAvailabilityStatus | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Load data status on mount
  useEffect(() => {
    loadDataStatus();
  }, []);

  // Load profitability data when vehicles or time range changes
  useEffect(() => {
    if (selectedVehicles.length > 0) {
      loadProfitabilityData();
    } else {
      setVehicleData([]);
      setFleetSummary(null);
    }
  }, [selectedVehicles, timeRange]);

  const loadDataStatus = async () => {
    try {
      const status = await getProfitabilityStatus();
      setDataStatus(status);
    } catch (err) {
      console.error('Failed to load profitability status:', err);
    }
  };

  const loadProfitabilityData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const startDate = new Date(timeRange.from).toISOString().split('T')[0];
      const endDate = new Date(timeRange.to).toISOString().split('T')[0];
      
      // Get data for each vehicle for the selected date (using start date as reference)
      const promises = selectedVehicles.map(vehicle =>
        getDailyProfitability(vehicle, startDate, 'v1_basic')
          .catch(err => {
            console.warn(`Failed to fetch profitability for ${vehicle}:`, err);
            return null;
          })
      );
      
      const results = await Promise.all(promises);
      const validResults = results.filter((r): r is DailyProfitability => r !== null);
      setVehicleData(validResults);
      
      // Get fleet summary if multiple vehicles
      if (selectedVehicles.length > 1) {
        try {
          const summary = await getFleetProfitabilitySummary(startDate, endDate, 'v1_basic');
          setFleetSummary(summary);
        } catch (err) {
          console.warn('Failed to fetch fleet summary:', err);
        }
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profitability data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (selectedVehicles.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-success" />
          <h3 className="text-lg font-semibold text-white">Profitability Analysis</h3>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Select vehicles to view profitability data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border hover:border-success hover:shadow-lg hover:shadow-success/30 transition-all">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-success" />
            <h3 className="text-lg font-semibold text-white">Profitability Analysis</h3>
            {dataStatus && (
              <Badge tone="info">
                {dataStatus.calculation_version}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={() => setShowDetails(!showDetails)}
            className="h-auto px-0 gap-1 text-xs hover:text-foreground"
          >
            <Info className="w-4 h-4" />
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </div>
      </div>

      {/* Data Status Info */}
      {showDetails && dataStatus && (
        <div className="p-4 border-b border-border bg-card/50">
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground mb-2">Data Source Status:</p>
            {Object.entries(dataStatus.data_sources).map(([key, source]) => (
              <div key={key} className="flex items-center gap-2 text-xs">
                {source.available ? (
                  <Check className="w-3 h-3 text-success" />
                ) : (
                  <X className="w-3 h-3 text-destructive" />
                )}
                <span className="text-muted-foreground">{source.description}</span>
                <Badge tone={source.status === 'connected' ? 'success' : 'destructive'} className="ml-auto">
                  {source.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="text-center text-muted-foreground py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-success mx-auto mb-2"></div>
            <p className="text-sm">Loading profitability data...</p>
          </div>
        ) : error ? (
          <div className="text-center text-destructive py-8">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">{error}</p>
          </div>
        ) : vehicleData.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No profitability data available for selected vehicles</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Fleet Summary */}
            {fleetSummary && selectedVehicles.length > 1 && (
              <div className="bg-secondary/30 rounded-lg p-4 border border-border">
                <p className="text-sm font-medium text-foreground mb-3">Fleet Summary</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Revenue</p>
                    <p className="text-lg font-bold text-white">
                      {formatCurrency(fleetSummary.fleet_summary.total_revenue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Profit</p>
                    <p className="text-lg font-bold text-success">
                      {formatCurrency(fleetSummary.fleet_summary.net_profit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Fleet Margin</p>
                    <p className="text-lg font-bold text-white">
                      {formatPercent(fleetSummary.fleet_summary.avg_margin_percent)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Active Vehicles</p>
                    <p className="text-lg font-bold text-white">
                      {fleetSummary.fleet_summary.vehicle_count}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Individual Vehicle Data */}
            <div className="space-y-3">
              {vehicleData.map((data) => (
                <div
                  key={`${data.vehicle_registration}-${data.date}`}
                  className="bg-secondary/20 rounded-lg p-4 border border-border hover:border-success transition-colors cursor-pointer"
                  onClick={() => setSelectedVehicle(selectedVehicle === data.vehicle_registration ? null : data.vehicle_registration)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-white">{data.vehicle_registration}</p>
                      <p className="text-xs text-muted-foreground">{new Date(data.date).toLocaleDateString('en-GB')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-success">
                        {formatCurrency(data.net_profit)}
                      </p>
                      <div className="flex items-center gap-1 justify-end">
                        {data.profit_margin_percent >= 60 ? (
                          <TrendingUp className="w-3 h-3 text-success" />
                        ) : (
                          <TrendingDown className="w-3 h-3 text-warning" />
                        )}
                        <p className="text-sm text-foreground">
                          {formatPercent(data.profit_margin_percent)} margin
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  {selectedVehicle === data.vehicle_registration && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Revenue</p>
                          <p className="text-white font-medium">
                            {formatCurrency(data.revenue.total)}
                            <span className="text-xs text-muted-foreground ml-1">
                              ({data.revenue.order_count} orders)
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total Costs</p>
                          <p className="text-destructive font-medium">
                            -{formatCurrency(data.costs.total)}
                          </p>
                        </div>
                      </div>

                      <div className="text-xs space-y-1 bg-card/50 p-2 rounded">
                        <p className="text-foreground font-medium mb-1">Cost Breakdown:</p>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fuel ({data.costs.fuel.source}):</span>
                          <span className="text-white">-{formatCurrency(data.costs.fuel.total)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Driver ({data.costs.driver.source}):</span>
                          <span className="text-white">-{formatCurrency(data.costs.driver.total)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fixed Overhead:</span>
                          <span className="text-white">-{formatCurrency(data.costs.overhead.total)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Empty Miles:</span>
                          <span className="text-white">{data.costs.mileage.empty_miles.toFixed(1)} mi</span>
                        </div>
                      </div>

                      {/* Qargo Comparison */}
                      {data.qargo_estimate && (
                        <div className="text-xs bg-ring/20 p-2 rounded border border-ring/30">
                          <p className="text-ring font-medium mb-1">vs Qargo Estimate:</p>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Qargo Profit:</span>
                            <span className="text-white">{formatCurrency(data.qargo_estimate.profit)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Difference:</span>
                            <span className={(data.net_profit - data.qargo_estimate.profit) < 0 ? 'text-destructive' : 'text-success'}>
                              {formatCurrency(data.net_profit - data.qargo_estimate.profit)}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Calculation Info */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                        <Info className="w-3 h-3" />
                        <span>
                          {data.confidence.level} confidence ({data.confidence.score}%)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
